import type {
  CrremDisplayMetric,
  CrremSettings,
  CrremTrendMethod,
} from "features/energy-analysis-dashboard/model/types";

import {
  parseDemandPointRows,
  parseDemandUnit,
  parseLastReportedYear,
} from "features/energy-analysis-dashboard/lib/parseCrremApi";
import { CRREM_DISPLAY_METRIC, isCrremTrendMethod } from "features/energy-analysis-dashboard/model/constants";

import type {
  CrremCorrectionFlags,
  CrremDashboardMetricBlock,
  CrremDashboardPathways,
  CrremDashboardPayload,
  CrremDashboardScenario,
  CrremDashboardTrend,
  CrremPathwayScenario,
  CrremPathwayTargetBlock,
} from "../model/types";

import { isBaselineCorrections } from "../model/constants";
import { formatCorrectionLabel, formatCrremScenarioLabel } from "./formatScenarioLabel";
import { parseCrremPathwayIncomplete, parseYearlyPoints } from "./parseCrremPathwayApi";

const PATHWAY_TARGET_KEYS = ["target_1_5", "target_2_0"] as const;

const emptyPathwayTarget = (unit = ""): CrremPathwayTargetBlock => ({
  target_1_5: [],
  target_2_0: [],
  unit,
});

const emptyPathways = (): CrremDashboardPathways => ({
  co2: emptyPathwayTarget(),
  energy: emptyPathwayTarget(),
});

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const readString = (value: unknown) => (typeof value === "string" && value.length > 0 ? value : undefined);

const readBoolean = (value: unknown) => (typeof value === "boolean" ? value : false);

const readPositiveNumber = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined;

const parseIncludes = (value: unknown) =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string" && item.length > 0) : [];

const mergeDemandRows = (
  baseline: CrremDashboardMetricBlock["baseline"],
  corrected: CrremDashboardMetricBlock["corrected"]
) => {
  if (corrected.length === 0) return baseline;
  if (baseline.length === 0) return corrected;
  const rows = new Map(baseline.map((row) => [row.id, row]));
  corrected.forEach((row) => rows.set(row.id, row));
  return [...rows.values()].sort((left, right) => left.year - right.year || left.id.localeCompare(right.id));
};

const parseCorrections = (value: unknown): CrremCorrectionFlags => {
  if (!isRecord(value)) return { climate: false, coverage: false, vacancy: false };
  return {
    climate: readBoolean(value.climate),
    coverage: readBoolean(value.coverage),
    vacancy: readBoolean(value.vacancy),
  };
};

const parseMetricBlock = (value: unknown, fallbackUnit: string): CrremDashboardMetricBlock => {
  if (!isRecord(value)) {
    return { baseline: [], corrected: [], correctedIncludes: [], rows: [], unit: fallbackUnit };
  }
  const baseline = parseDemandPointRows(value.baseline);
  const corrected = parseDemandPointRows(value.corrected);
  return {
    baseline,
    corrected,
    correctedIncludes: parseIncludes(value.correctedIncludes),
    rows: mergeDemandRows(baseline, corrected),
    unit: parseDemandUnit(value, fallbackUnit),
  };
};

const correctionId = (corrections: CrremCorrectionFlags) =>
  `${corrections.coverage ? "1" : "0"}${corrections.climate ? "1" : "0"}${corrections.vacancy ? "1" : "0"}`;

const parseScenario = (value: unknown): CrremDashboardScenario | undefined => {
  if (!isRecord(value)) return undefined;
  const corrections = parseCorrections(value.corrections);
  const id = readString(value.id) ?? correctionId(corrections);
  return {
    co2: parseMetricBlock(value.co2, "kg"),
    corrections,
    energy: parseMetricBlock(value.energy, "kWh"),
    id,
    label: formatCorrectionLabel(corrections, readString(value.label) ?? id),
  };
};

const parseScenarios = (value: unknown) =>
  Array.isArray(value)
    ? value.map(parseScenario).filter((scenario): scenario is CrremDashboardScenario => Boolean(scenario))
    : [];

const parseTrend = (value: unknown): CrremDashboardTrend | undefined => {
  if (!isRecord(value)) return undefined;
  const scenarios = parseScenarios(value.scenarios);
  if (scenarios.length === 0) return undefined;
  return {
    scenarios,
    trendMethod: isCrremTrendMethod(value.trendMethod) ? value.trendMethod : "average",
  };
};

const parseTrends = (payload: Record<string, unknown>): CrremDashboardTrend[] => {
  if (Array.isArray(payload.trends)) {
    return payload.trends.map(parseTrend).filter((trend): trend is CrremDashboardTrend => Boolean(trend));
  }
  const scenarios = parseScenarios(payload.scenarios);
  return scenarios.length > 0 ? [{ scenarios, trendMethod: "average" }] : [];
};

const parsePathwayTarget = (value: unknown, fallbackUnit: string): CrremPathwayTargetBlock => {
  if (!isRecord(value)) return emptyPathwayTarget(fallbackUnit);
  return {
    target_1_5: parseYearlyPoints(value.target_1_5),
    target_2_0: parseYearlyPoints(value.target_2_0),
    unit: readString(value.unit) ?? fallbackUnit,
  };
};

const parsePathways = (value: unknown): CrremDashboardPathways => {
  if (!isRecord(value)) return emptyPathways();
  return {
    co2: parsePathwayTarget(value.co2, "kgCO2/m²"),
    energy: parsePathwayTarget(value.energy, "kWh/m²"),
  };
};

const parseFloorArea = (payload: Record<string, unknown>) => {
  if (isRecord(payload.resolvedConfig)) return readPositiveNumber(payload.resolvedConfig.floorAreaM2);
  return readPositiveNumber(payload.floorAreaM2);
};

export const emptyCrremDashboardPayload = (): CrremDashboardPayload => ({
  missing: [],
  pathways: emptyPathways(),
  trends: [],
});

export const parseCrremDashboardPayload = (payload: unknown): CrremDashboardPayload => {
  const incomplete = parseCrremPathwayIncomplete(payload);
  if (incomplete) return { ...emptyCrremDashboardPayload(), missing: incomplete.missing };
  if (!isRecord(payload)) return emptyCrremDashboardPayload();
  return {
    floorAreaM2: parseFloorArea(payload),
    lastReportedYear: parseLastReportedYear(payload),
    missing: [],
    pathways: parsePathways(payload.pathways),
    trends: parseTrends(payload),
  };
};

export const pickTrendScenarios = (payload: CrremDashboardPayload | undefined, trendMethod?: CrremTrendMethod) => {
  const trends = payload?.trends ?? [];
  const match = trendMethod ? trends.find((trend) => trend.trendMethod === trendMethod) : undefined;
  return (match ?? trends[0])?.scenarios ?? [];
};

export const pickDashboardMetric = (scenario: CrremDashboardScenario, metric: CrremDisplayMetric) =>
  metric === CRREM_DISPLAY_METRIC.kwh ? scenario.energy : scenario.co2;

export const pickPathwayTargets = (payload: CrremDashboardPayload | undefined, metric: CrremDisplayMetric) =>
  metric === CRREM_DISPLAY_METRIC.kwh ? payload?.pathways.energy : payload?.pathways.co2;

export const toPathwayScenarios = (block?: CrremPathwayTargetBlock): CrremPathwayScenario[] => {
  if (!block) return [];
  return PATHWAY_TARGET_KEYS.filter((key) => block[key].length > 0).map((key) => ({
    key,
    label: formatCrremScenarioLabel(key),
    points: block[key],
  }));
};

export const pickScenarioBySettings = (scenarios: CrremDashboardScenario[], settings?: CrremSettings) => {
  if (!settings) return scenarios.find((scenario) => isBaselineCorrections(scenario.corrections)) ?? scenarios[0];
  return (
    scenarios.find(
      (scenario) =>
        scenario.corrections.climate === settings.climateCorrectionEnabled &&
        scenario.corrections.coverage === settings.coverageCorrectionEnabled &&
        scenario.corrections.vacancy === settings.vacancyCorrectionEnabled
    ) ??
    scenarios.find((scenario) => isBaselineCorrections(scenario.corrections)) ??
    scenarios[0]
  );
};

export const pickDemandFromDashboard = (
  payload: CrremDashboardPayload | undefined,
  settings: CrremSettings | undefined,
  fallbackUnit: string
) => {
  const scenario = pickScenarioBySettings(pickTrendScenarios(payload, settings?.trendMethod), settings);
  const block = scenario && settings ? pickDashboardMetric(scenario, settings.displayMetric) : undefined;
  return {
    lastReportedYear: payload?.lastReportedYear,
    rows: block?.rows ?? [],
    unit: block?.unit || fallbackUnit,
  };
};

export const hasCrremDashboardRows = (payload?: CrremDashboardPayload) =>
  (payload?.trends ?? []).some((trend) =>
    trend.scenarios.some((scenario) => scenario.energy.rows.length > 0 || scenario.co2.rows.length > 0)
  );

export const hasCrremPathwayTargets = (payload?: CrremDashboardPayload) => {
  const energy = payload?.pathways.energy;
  const co2 = payload?.pathways.co2;
  return Boolean(
    energy?.target_1_5.length || energy?.target_2_0.length || co2?.target_1_5.length || co2?.target_2_0.length
  );
};

export const hasCrremDashboardData = (payload?: CrremDashboardPayload) =>
  hasCrremDashboardRows(payload) || hasCrremPathwayTargets(payload);

export const dashboardMetricUnit = (
  scenario: CrremDashboardScenario | undefined,
  metric: CrremDisplayMetric,
  fallback: string
) => (scenario ? pickDashboardMetric(scenario, metric).unit || fallback : fallback);
